/**
 * Per-page SEO meta-tag generator (AF-136 — OG / Twitter Card / JSON-LD).
 *
 * The marketing SPA was title-only (`useDocumentTitle`); search engines and
 * social cards saw no per-page description, canonical URL, Open Graph, Twitter
 * Card, or structured data. This pure builder produces the full per-page meta
 * set so a render-time hook (or a future SSG step) can inject it into
 * `<head>`. It composes the existing single-sources-of-truth — `SITE_BASE_URL`
 * for absolute/canonical URLs and `TITLE_SUFFIX` for the brand-suffixed title
 * — so there is no duplicated domain string or title convention.
 *
 * Pure TS, no React, no deps — trivially unit-testable. The head-injection
 * hook (`useSeoMeta`) and per-page wiring are the consumers (follow-on).
 *
 * Deliberately omits `twitter:site`/`twitter:creator` (a handle) — Syncanix has
 * no committed handle in `product/`, and fabricating one would violate the
 * no-dummy-data bar. The card still renders without it.
 */
import { UC_TIERS, DEFAULT_LOCALE, isSupportedLocale, type Locale } from '@syncanix/shared';

import { localizedPath } from '@/i18n/locale-path';
import { DOCS_SEARCH_PATH, DOCS_SEARCH_QUERY_PARAM } from '@/pages/docs/docs-search-param';

import { SITE_BASE_URL } from '../site-url';
import { TITLE_SUFFIX } from './use-document-title';

export type PageMetaType = 'website' | 'article';

/** A single `hreflang` alternate: a BCP-47 (or `x-default`) tag + its href. */
export interface AlternateLink {
  /** `hreflang` value — a locale code (`en`/`es`/…) or `x-default`. */
  readonly hreflang: string;
  /** Site-relative path or absolute URL for that locale's version of the page. */
  readonly href: string;
}

export interface PageMetaInput {
  /** Page-specific title (the brand suffix is appended unless `fullTitle` is given). */
  readonly title: string;
  readonly description: string;
  /** Site-relative path beginning with `/` (e.g. `/vs/sierra`), or `/` for home. */
  readonly path: string;
  /** Override the composed `${title} ${TITLE_SUFFIX}` with a bespoke full title (the home page). */
  readonly fullTitle?: string;
  /** `website` (default) or `article`. */
  readonly type?: PageMetaType;
  /** Social-card image — absolute, or site-relative (resolved against `SITE_BASE_URL`). */
  readonly imageUrl?: string;
  /** ISO-8601 publish time, for `article:published_time` + JSON-LD `datePublished` (articles). */
  readonly publishedTime?: string;
  /**
   * Q&A pairs to emit as a schema.org `FAQPage` JSON-LD block (e.g. the pricing
   * page). Omit on pages without genuine on-page FAQ content — never synthesise.
   */
  readonly faq?: readonly FaqItem[];
  /**
   * The locale this page is rendered in. Drives `og:locale`. Defaults to the EN
   * baseline. Set by every per-locale route via `useLocalizedSeoMeta` (ADR-0133,
   * site-wide; originally docs-only per ADR-0075).
   */
  readonly locale?: Locale;
  /**
   * `hreflang` alternates for a page that exists in multiple locales. Emit one
   * entry per published locale plus an `x-default`. Omit for single-locale pages
   * — never fabricate alternates that don't resolve.
   */
  readonly alternates?: readonly AlternateLink[];
  /**
   * Localized labels for the `BreadcrumbList` JSON-LD (the "Home" root crumb +
   * the section crumbs). Defaults to the English {@link SECTION_LABELS} / "Home"
   * so single-locale callers are unaffected; per-locale pages pass translated
   * labels so the structured-data breadcrumb is in-language (GA-13 / ADR-0133).
   */
  readonly breadcrumbLabels?: BreadcrumbLabels;
}

/** Localized breadcrumb labels: the "Home" root + per-section-segment names. */
export interface BreadcrumbLabels {
  readonly home: string;
  /** Keyed by first path segment (`trust`, `vs`, …); falls back to the English default. */
  readonly sections: Readonly<Record<string, string>>;
}

/** A single FAQ entry for an `FAQPage` JSON-LD block. */
export interface FaqItem {
  readonly question: string;
  readonly answer: string;
}

/** A renderable head tag: exactly one of `name`/`property`, plus `content`. */
export interface MetaTag {
  readonly name?: string;
  readonly property?: string;
  readonly content: string;
}

export interface PageMeta {
  readonly title: string;
  readonly description: string;
  readonly canonicalUrl: string;
  readonly metaTags: readonly MetaTag[];
  /** The primary page entity (`WebPage`/`Article`) — also `jsonLdBlocks[0]`. */
  readonly jsonLd: Record<string, unknown>;
  /**
   * Every JSON-LD block to emit, in order: the primary `jsonLd` first, then any
   * proportionate rich-results extras (top-level `Organization` on the home
   * page, a `BreadcrumbList` on nested routes, an `FAQPage` where Q&A exists).
   */
  readonly jsonLdBlocks: readonly Record<string, unknown>[];
  /** Resolved (absolute-href) `hreflang` alternate links; empty for single-locale pages. */
  readonly alternateLinks: readonly AlternateLink[];
}

const ABSOLUTE_URL_RE = /^https?:\/\//;

/**
 * The site-wide social card (`scripts/generate-og-image.ts` writes it into
 * `public/`). Dimensions mirror `build-og-image.ts` — duplicated as literals
 * because that builder imports `node:zlib` and this module ships in the client
 * bundle; a unit test pins the parity.
 */
export const DEFAULT_OG_IMAGE_PATH = '/og-image.png';
export const DEFAULT_OG_IMAGE_WIDTH = 1200;
export const DEFAULT_OG_IMAGE_HEIGHT = 630;

/**
 * Open Graph `og:locale` value per supported locale. The whole marketing site is
 * per-locale (ADR-0133), so `buildPageMeta` resolves this from the page's
 * `locale` input.
 */
const OG_LOCALE_BY_LOCALE: Record<Locale, string> = {
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  de: 'de_DE',
  ar: 'ar_AR',
  he: 'he_IL',
};

/**
 * Marker attribute on every head tag the build-time prerender (SSG) injects.
 * The client hydration hook (`useSeoMeta`) removes this server baseline exactly
 * once on first mount before taking over client-managed injection, so the
 * initial document never carries a duplicate description / canonical / JSON-LD.
 */
export const HEAD_SSR_ATTR = 'data-seo-ssr';

/** The publisher node reused by the page entity and the home Organization. */
const SYNCANIX_ORG = { '@type': 'Organization', name: 'Syncanix', url: SITE_BASE_URL } as const;

/**
 * The entry plan, from the shared pricing SOT — never a literal. `/pricing`
 * renders the same source, so the structured price and the visible price cannot
 * drift apart.
 */
const ENTRY_TIER = UC_TIERS.starter;

/**
 * Human labels for the first path segment of a nested route, used as the
 * intermediate `BreadcrumbList` crumb. Only the handful of real section roots
 * need an entry; an unknown segment falls back to a title-cased slug. Keep in
 * sync with the section index routes in `App.tsx` / `site-routes.ts`.
 */
const SECTION_LABELS: Record<string, string> = {
  trust: 'Trust Center',
  vs: 'Comparisons',
  'one-pagers': 'One-pagers',
  pricing: 'Pricing',
  docs: 'Documentation',
};

/** Resolve a site-relative path (or pass through an absolute URL) to an absolute URL. */
function toAbsoluteUrl(pathOrUrl: string): string {
  if (ABSOLUTE_URL_RE.test(pathOrUrl)) {
    return pathOrUrl;
  }
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  // Home page renders as the bare origin (no trailing slash), matching SITE_BASE_URL.
  return path === '/' ? SITE_BASE_URL : `${SITE_BASE_URL}${path}`;
}

/**
 * True for the home page — the English root `/` AND each per-locale home
 * (`/es`, `/fr`, …, ADR-0133), but never a one-segment content path like
 * `/pricing` or a deeper localized path like `/es/pricing`.
 */
function isHomePath(path: string): boolean {
  if (path === '/') return true;
  const segments = path.split('/').filter((s) => s.length > 0);
  return (
    segments.length === 1 && segments[0] !== DEFAULT_LOCALE && isSupportedLocale(segments[0] ?? '')
  );
}

function titleCaseSlug(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Derive a `BreadcrumbList` for a nested route (≥ 2 path segments): Home →
 * section → current page. Returns `null` for the home page and top-level pages
 * (a one-hop Home → page trail carries no signal). The leaf label is the page's
 * own name (`leafName`); intermediate crumbs use {@link SECTION_LABELS}.
 */
function buildBreadcrumb(
  path: string,
  leafName: string,
  canonicalUrl: string,
  labels?: BreadcrumbLabels,
): Record<string, unknown> | null {
  const allSegments = path.split('/').filter((s) => s.length > 0);
  // A leading non-English locale segment (`/es/docs/…`, ADR-0075) is a URL
  // prefix, not a breadcrumb section: carry it on every crumb's URL but never
  // treat it as a hop. English paths have no such prefix.
  const localePrefix =
    allSegments[0] !== undefined &&
    allSegments[0] !== DEFAULT_LOCALE &&
    isSupportedLocale(allSegments[0])
      ? `/${allSegments[0]}`
      : '';
  const segments = localePrefix === '' ? allSegments : allSegments.slice(1);
  if (segments.length < 2) {
    return null;
  }

  const crumbs: { name: string; url: string }[] = [
    // The Home crumb keeps the locale prefix so the whole trail stays in-locale
    // (a /es/… page's Home is https://syncanix.com/es, not the EN root).
    { name: labels?.home ?? 'Home', url: `${SITE_BASE_URL}${localePrefix}` },
  ];
  let acc = '';
  for (const seg of segments.slice(0, -1)) {
    acc += `/${seg}`;
    crumbs.push({
      name: labels?.sections[seg] ?? SECTION_LABELS[seg] ?? titleCaseSlug(seg),
      url: `${localePrefix}${acc}`,
    });
  }
  crumbs.push({ name: leafName, url: canonicalUrl });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: c.url.startsWith('http') ? c.url : `${SITE_BASE_URL}${c.url}`,
    })),
  };
}

/** schema.org `FAQPage` block from genuine on-page Q&A. */
function buildFaqPage(faq: readonly FaqItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

/**
 * schema.org `SoftwareApplication` + its `Offer`, on the home page.
 *
 * The rest of this module deliberately stays minimal, and that stance is
 * unchanged for the markup that invites gaming — no `Review`, no
 * `aggregateRating`, no fabricated `ratingCount`. This is different in kind:
 * the price and the currency are facts, read from the shared pricing SOT, and
 * an answer engine asked "what does Syncanix cost" should get the real number
 * rather than infer one from a scraped page.
 *
 * The node has never carried a trial length — this comment claimed it did until
 * ADR-0489's sweep went looking. Worth noting rather than quietly deleting: a
 * doc comment that describes markup the code does not emit is the same class of
 * defect as markup that over-claims, because the next person reasons from the
 * comment. Nothing to remove here; there is now nothing to remove ANYWHERE,
 * since Syncanix no longer offers a trial to advertise.
 *
 * `price` is the entry tier. Advertising the cheapest real plan is the honest
 * reading of `offers` for a laddered product; the ladder itself is on
 * `/pricing`, which the offer's `url` points at.
 *
 * Re-verified against ADR-0442 (posted price per interaction): the Offer quotes
 * the SUBSCRIPTION price, which that ADR did not move — tier prices and included
 * allowances are unchanged, only how an interaction inside the allowance is
 * priced. The per-interaction menu deliberately stays OUT of this node: `Offer`
 * has no vocabulary for "one of four prices depending on what the request did",
 * and forcing it into `price` would publish a machine-readable claim the product
 * does not make.
 */
function buildSoftwareApplication(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Syncanix',
    url: SITE_BASE_URL,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    publisher: { ...SYNCANIX_ORG },
    offers: {
      '@type': 'Offer',
      price: String(ENTRY_TIER.monthlyPriceUsd),
      priceCurrency: 'USD',
      url: `${SITE_BASE_URL}/pricing`,
      availability: 'https://schema.org/InStock',
    },
  };
}

/**
 * schema.org `WebSite` + the one action this site can actually perform.
 *
 * **Why an action node exists at all.** The widget-discovery audit's first
 * real-site harvest run (§14.2) read 200 prerendered documents of this very site
 * and found zero `potentialAction`: Syncanix sells a JSON-LD action-discovery
 * mechanism and declared nothing for it to find on its own website. Buyers now
 * score this directly (Cloudflare's Agent Readiness Score), so the gap was both
 * a credibility problem and a self-inflicted blind spot.
 *
 * **Why exactly one.** The same audit's standing rule is that a declared
 * capability must work. Docs search is the one thing an agent can genuinely
 * drive here, and only since it became URL-addressable — declaring it before
 * that would have published a machine-readable endpoint that answers nothing,
 * which is the failure this product exists to prevent for customers. Everything
 * else on this site is prose. No `OrderAction`, no `SubscribeAction`, for the
 * same reason the module carries no `aggregateRating`.
 *
 * The target LOCALIZES: a Spanish home advertising `/docs?q=` would send a
 * Spanish visitor to the English docs, so the path goes through `localizedPath`
 * exactly as the routes and the sitemap do. `query-input` uses the string
 * shorthand rather than an expanded `PropertyValueSpecification` because that is
 * what real markup overwhelmingly ships.
 */
function buildWebSite(canonicalUrl: string, locale: Locale): Record<string, unknown> {
  const searchPath = localizedPath(DOCS_SEARCH_PATH, locale);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Syncanix',
    url: canonicalUrl,
    publisher: { ...SYNCANIX_ORG },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_BASE_URL}${searchPath}?${DOCS_SEARCH_QUERY_PARAM}={search_term_string}`,
        httpMethod: 'GET',
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Build the complete per-page SEO meta set. Throws on malformed input (empty
 * title/description, a path that is not site-relative) so a misconfigured page
 * is caught at build/test time rather than shipping blank cards.
 */
export function buildPageMeta(input: PageMetaInput): PageMeta {
  if (input.fullTitle === undefined && input.title.trim() === '') {
    throw new Error('buildPageMeta: title (or fullTitle) must be non-empty');
  }
  if (input.description.trim() === '') {
    throw new Error('buildPageMeta: description must be non-empty');
  }
  if (!input.path.startsWith('/')) {
    throw new Error('buildPageMeta: path must be site-relative (start with "/")');
  }

  const title = input.fullTitle ?? `${input.title} ${TITLE_SUFFIX}`;
  const { description } = input;
  const canonicalUrl = toAbsoluteUrl(input.path);
  const type: PageMetaType = input.type ?? 'website';
  // Every page carries a social-card image (iter 29): pages may pass a bespoke
  // `imageUrl`; otherwise the site-wide branded card (generated into public/
  // by `scripts/generate-og-image.ts`). Without one, shares on Slack/LinkedIn/X
  // rendered text-only stubs and twitter:card stayed on the small variant.
  const image = toAbsoluteUrl(input.imageUrl ?? DEFAULT_OG_IMAGE_PATH);
  const locale = input.locale ?? DEFAULT_LOCALE;

  // Resolve hreflang alternates to absolute hrefs (deduped, stable order).
  const alternateLinks: AlternateLink[] = (input.alternates ?? []).map((alt) => ({
    hreflang: alt.hreflang,
    href: toAbsoluteUrl(alt.href),
  }));

  const metaTags: MetaTag[] = [
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: type },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:site_name', content: 'Syncanix' },
    { property: 'og:locale', content: OG_LOCALE_BY_LOCALE[locale] },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
  ];
  // og:locale:alternate for each *other* published locale (skips x-default and self).
  for (const alt of alternateLinks) {
    if (
      alt.hreflang !== 'x-default' &&
      isSupportedLocale(alt.hreflang) &&
      alt.hreflang !== locale
    ) {
      metaTags.push({
        property: 'og:locale:alternate',
        content: OG_LOCALE_BY_LOCALE[alt.hreflang],
      });
    }
  }
  metaTags.push({ property: 'og:image', content: image });
  metaTags.push({ name: 'twitter:image', content: image });
  // Dimensions + alt only for the site-wide card (a bespoke imageUrl's
  // dimensions are unknown here — emitting wrong ones is worse than none).
  if (input.imageUrl === undefined) {
    metaTags.push({ property: 'og:image:width', content: String(DEFAULT_OG_IMAGE_WIDTH) });
    metaTags.push({ property: 'og:image:height', content: String(DEFAULT_OG_IMAGE_HEIGHT) });
    metaTags.push({ property: 'og:image:alt', content: 'Syncanix' });
  }
  if (type === 'article' && input.publishedTime !== undefined) {
    metaTags.push({ property: 'article:published_time', content: input.publishedTime });
  }

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': type === 'article' ? 'Article' : 'WebPage',
    name: title,
    description,
    url: canonicalUrl,
    publisher: { ...SYNCANIX_ORG },
    image,
    ...(type === 'article' ? { headline: title } : {}),
    ...(type === 'article' && input.publishedTime !== undefined
      ? { datePublished: input.publishedTime }
      : {}),
  };

  // Proportionate rich-results extras. Google de-emphasises structured data for
  // generative AI, so this stays minimal — and stays clear of the markup that
  // invites gaming (no `Review`, no `aggregateRating`). Factual product and
  // price data is the exception: an answer engine asked what this costs should
  // read the real number from the pricing SOT rather than infer one.
  const jsonLdBlocks: Record<string, unknown>[] = [jsonLd];

  // Top-level Organization entity, on the home page only — including the
  // per-locale homes (`/es`, `/fr`, …) so localized homes carry the same rich
  // entity as the English home (ADR-0133).
  if (isHomePath(input.path)) {
    jsonLdBlocks.push({
      '@context': 'https://schema.org',
      ...SYNCANIX_ORG,
      logo: `${SITE_BASE_URL}/apple-touch-icon.png`,
    });
    jsonLdBlocks.push(buildSoftwareApplication());
    jsonLdBlocks.push(buildWebSite(canonicalUrl, locale));
  }

  // BreadcrumbList for nested routes (Home → section → page).
  const breadcrumb = buildBreadcrumb(input.path, input.title, canonicalUrl, input.breadcrumbLabels);
  if (breadcrumb !== null) {
    jsonLdBlocks.push(breadcrumb);
  }

  // FAQPage where genuine on-page Q&A exists (e.g. /pricing).
  if (input.faq !== undefined && input.faq.length > 0) {
    jsonLdBlocks.push(buildFaqPage(input.faq));
  }

  return { title, description, canonicalUrl, metaTags, jsonLd, jsonLdBlocks, alternateLinks };
}

/** Serialize the primary page JSON-LD for a `<script type="application/ld+json">` body. */
export function renderJsonLd(meta: PageMeta): string {
  return JSON.stringify(meta.jsonLd);
}

/**
 * Serialize one JSON-LD block for safe embedding in a `<script>` element:
 *  - `<` → `<` so a stray `</script>` in any field can't break out, and
 *  - U+2028 / U+2029 (LINE/PARAGRAPH SEPARATOR) → escaped, because they are
 *    valid in JSON but are ECMAScript line terminators that can break an inline
 *    `<script>` body.
 */
export function serializeJsonLdBlock(block: Record<string, unknown>): string {
  return JSON.stringify(block)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/** Escape a string for safe use inside an HTML double-quoted attribute value. */
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Render the per-page `<head>` fragment — description, OG/Twitter, canonical,
 * and JSON-LD — as an HTML string for the build-time prerender (SSG) to splice
 * into the document head. Mirror of what `useSeoMeta` injects at runtime, so a
 * route's head is identical whether it arrives via the static file (crawlers,
 * non-JS bots) or via client navigation.
 *
 * The `<title>` is deliberately NOT included: the prerender replaces the HTML
 * template's `<title>` element separately (a singular element, not appended).
 * Every tag carries {@link HEAD_SSR_ATTR} so the client hook can drop this
 * baseline once on hydration.
 */
export function renderHeadHtml(meta: PageMeta): string {
  const lines: string[] = [];
  for (const t of meta.metaTags) {
    const attr =
      t.name !== undefined
        ? `name="${escapeAttr(t.name)}"`
        : `property="${escapeAttr(t.property ?? '')}"`;
    lines.push(`<meta ${attr} content="${escapeAttr(t.content)}" ${HEAD_SSR_ATTR}>`);
  }
  lines.push(`<link rel="canonical" href="${escapeAttr(meta.canonicalUrl)}" ${HEAD_SSR_ATTR}>`);
  for (const alt of meta.alternateLinks) {
    lines.push(
      `<link rel="alternate" hreflang="${escapeAttr(alt.hreflang)}" ` +
        `href="${escapeAttr(alt.href)}" ${HEAD_SSR_ATTR}>`,
    );
  }
  for (const block of meta.jsonLdBlocks) {
    lines.push(
      `<script type="application/ld+json" ${HEAD_SSR_ATTR}>${serializeJsonLdBlock(block)}</script>`,
    );
  }
  return lines.join('\n    ');
}
